import { Box, Divider, Group, Text } from '@byecode/ui'
import type { DataSourceAbstract, Field } from '@lighthouse/core'
import type { ClickTriggerConfigure, FlowNode, LoopTimesConfig, VariableTriggerConfigure } from '@lighthouse/shared'
import {
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    ListItem4ByecodeUi,
    OUTPUT_SINGLE_DATA_NODES,
    useAtomData,
    VariableSelect
} from '@lighthouse/shared'
import { useAtomValue } from 'jotai'
import { find } from 'rambda'
import React, { useMemo } from 'react'

import { workflowListAtom } from '@/atoms/workflow/state'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import * as SC from '../../styles'
import { SettingSubProcessContent } from '../component/SettingSubProcessContent'
import { SubProcessSelector } from '../component/SubProcessSelector'

interface TimesConfigProps {
    dataSourceList: DataSourceAbstract[]
    allParentNodes: FlowNode[]
    nodeName?: string
    value?: Partial<LoopTimesConfig>
    onChange?: (value: Partial<LoopTimesConfig>) => void
}

export const TimesConfig: React.FC<TimesConfigProps> = ({ dataSourceList, allParentNodes, nodeName, value, onChange }) => {
    const workflows = useAtomValue(workflowListAtom)
    const workflowList = useAtomData(workflowListAtom)
    const numberField: Field = { id: '', name: '', dsId: '', type: 'number', innerType: 'NUMBER', number: {} }

    const subProcessId = value?.subProcessId

    const loop = useMemo(
        () => ({
            open: true,
            name: nodeName || ''
        }),
        [nodeName]
    )

    const userOption = useGetUserRecordOptions()
    const upstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: allParentNodes.filter(node => OUTPUT_SINGLE_DATA_NODES.has(node.data.nodeType)),
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, workflowList]
    )

    const allSingeParentNode = useMemo(
        () => allParentNodes.filter(node => OUTPUT_SINGLE_DATA_NODES.has(node.data.nodeType)),
        [allParentNodes]
    )

    const subProcessArgs = useMemo(() => {
        const workflow = find(item => item.id === subProcessId && item.type === 'subProcess', workflows)
        if (!workflow) {
            return
        }
        const nodeList = workflow.content?.nodes || []
        const variableNode = find(item => item.data.nodeType === 'ARG_TRIGGER', nodeList)
        if (!variableNode) {
            return
        }
        const { config } = variableNode.data as ClickTriggerConfigure | VariableTriggerConfigure
        if (!config || !config?.args) {
            return
        }
        return config.args
    }, [subProcessId, workflows])

    return (
        <>
            <Divider color="var(--color-gray-200)" />
            <Group label="循环参数">
                <SC.Tip>
                    “起始值”参数每次循环自动增加“步长”的值，当“起始值”参数大于“结束值”时，退出循环。最大循环次数为1000次，超出会自动停止循环。
                </SC.Tip>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>
                            <Text inline>起始值</Text>
                            <Text inline color="var(--color-gray-400)">
                                [数值]
                            </Text>
                        </SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <VariableSelect
                            field={numberField}
                            innerTypes={getInputInnerTypeMatch('NUMBER')}
                            value={value?.start}
                            disablePexels
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            onChange={start => onChange?.({ ...(value || {}), start })}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>
                            <Text inline>结束值</Text>
                            <Text inline color="var(--color-gray-400)">
                                [数值]
                            </Text>
                        </SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <VariableSelect
                            field={numberField}
                            innerTypes={getInputInnerTypeMatch('NUMBER')}
                            value={value?.end}
                            disablePexels
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            onChange={end => onChange?.({ ...(value || {}), end })}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>
                            <Text inline>步长</Text>
                            <Text inline color="var(--color-gray-400)">
                                [数值]
                            </Text>
                        </SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <VariableSelect
                            field={numberField}
                            innerTypes={getInputInnerTypeMatch('NUMBER')}
                            value={value?.step}
                            disablePexels
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            onChange={step => onChange?.({ ...(value || {}), step })}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
            <Divider color="var(--color-gray-200)" />
            <Group label="循环的子流程">
                <SubProcessSelector
                    workflows={workflows}
                    value={subProcessId}
                    onChange={val => onChange?.({ ...(value || {}), subProcessId: val, args: [] })}
                />
            </Group>
            {subProcessId && subProcessArgs && subProcessArgs.length > 0 && (
                <>
                    <Divider color="var(--color-gray-200)" />
                    <Group label="子流程参数值设置">
                        <SC.Tip>设置子流程的参数值，参数值将会传递到子流程中。</SC.Tip>
                        <Box px={8}>
                            {(() => {
                                const list = (value?.args || []) ?? []
                                const data =
                                    subProcessArgs.map(item => {
                                        const dataItem = find(d => d.id === item.id, list)
                                        return {
                                            ...item,
                                            value: dataItem?.value
                                        }
                                    }) || []
                                return (
                                    <SettingSubProcessContent
                                        data={data}
                                        loop={loop}
                                        parentNodes={allSingeParentNode}
                                        allParentNodes={allParentNodes}
                                        onChange={args => onChange?.({ ...(value || {}), args })}
                                    />
                                )
                            })()}
                        </Box>
                    </Group>
                </>
            )}
        </>
    )
}
